// StaffAppraisal.js — the appraisal record UI for one staff member.  (v7)
// One tab per year (completed years read-only; current year editable draft).
//
// v7 changes — WHITE FIELDS, BORDER SIGNALS, PER-TAB PROGRESS BARS:
//  - The pale-green fill on every manager input (v5) is GONE. All manager
//    fields are now WHITE; their state lives in the border instead:
//      2px solid black (#111827) → not yet typed in / nothing selected
//      2px solid green (#16A34A) → answered
//      2px solid grey  (#D1D5DB) → record locked, nothing to do
//    Field padding drops 1px per axis so the wider border doesn't resize
//    any input or shift the training/objectives table columns.
//  - The 0–5 OVERALL RATING selector follows the same rule: while nothing
//    is selected ALL six options carry the black attention border, so the
//    unanswered group reads as one outstanding item; once a rating is
//    picked that option takes the green border and a ✓, and the others
//    fall back to a quiet grey.
//  - Fields explicitly marked optional (the staff member's own signature)
//    use inpOpt() — green when filled, quiet grey when not. A black
//    attention border on a non-blocking box would send managers chasing it.
//  - EVERY SUB-TAB now has a PROGRESS BAR beneath its label, filling with
//    green as that section's required items are answered and going solid
//    full-width once complete. tabComplete() is derived from the new
//    tabProgress() over the SAME criteria as before, so the bar hitting
//    100% and the tab showing ✓ can never disagree, and no tab starts or
//    stops counting as complete differently from v6.
//  - Sub-tab buttons are white unless active (the bar carries the green
//    now) and the colour-code legend describes borders, not fills.
// v5: THREE-COLOUR SOURCE SCHEME cascaded from the embedded branch page:
//   pale green fields/pills = manager's sections (colour marks OWNERSHIP,
//   so locked completed years keep it); amber panels/blocks in italics =
//   the staff member's own pre-appraisal words (Pre-appraisal tab panel +
//   every PreAnswer/PreRatings quote block, previously indigo); pale blue
//   panel = the automated 12-month evidence pack. A legend row above the
//   sub-tabs explains the scheme. Sub-tab buttons keep their existing
//   green-complete / teal-outstanding behaviour untouched.
// Sub-tabs: Narrative & evidence → Pre-appraisal → Performance →
// Training & development → Objectives → Wellbeing & general → Sign-off →
// Follow-ups.
//
// v3 changes:
//  - FEATURES FROM THE "Appraisal and development planning" FORM added:
//    overall performance RATING (0–5 with definitions) + summary; review of
//    OBJECTIVES FROM THE LAST REVIEW (objective / how measured / results /
//    comments, seedable from the previous year's record); SMART objectives
//    gain "Support required" and "Review notes"; a structured TRAINING /
//    DEVELOPMENT PLAN table (need / aim / method / support / cost /
//    timescale); review TYPE (probation vs annual) + REVIEW PERIOD; and a
//    GENERAL section (areas of concern, suggestions for business
//    improvement, potential, further comments).
//  - PLACEHOLDERS: every field carries suggested assessor questions /
//    observations, community-pharmacy specific.
//  - FIXED DIALOGUE SIZE: the modal has a constant height so switching
//    tabs never makes the form jump around; each tab scrolls internally.
//  - v5.1: SNAPSHOT IN THE DOCUMENTS — the Print / Export-to-PDF document
//    now embeds the 12-month performance snapshot in section 1 (narrative,
//    every insight colour-keyed by polarity, and a key-figures line:
//    near misses vs cohort, incidents, attendance, SOPs, year-on-year).
//    The appraiser form (routes v5.1) shows the full pack read-only near
//    the top AND injects the relevant slices inline into each section —
//    engagement above "went well", near-miss/incident figures above
//    "areas to develop", SOP reading above training, attendance above
//    "areas of concern" — exactly mirroring the website's callout pattern.
//  - v5: EMAIL APPRAISER FORM — "✉ Email appraiser form" (drafts only)
//    emails the conducting manager a tokenised standalone form covering
//    the ENTIRE appraisal: it pulls the current record in (including the
//    pre-appraisal answers, shown read-only), autosaves every field into
//    the same staff_appraisals.sections this dialogue edits, and
//    "Complete & submit" locks the record with the evidence-pack snapshot
//    exactly like in-system completion, then kills the link. 30-day
//    expiry; authority stamped from the issuing user's role; the evidence
//    pack itself is never exposed externally. Requires
//    staff_appraisals_routes.js v5.
//  - v4.1: PRINT & EXPORT TO PDF — header buttons that render the ENTIRE
//    appraisal (every section, pre-appraisal answers & self-ratings,
//    sign-off, follow-ups, emails log, sent summary) as one print-ready
//    document headed with pharmacy / staff / role / dates / status.
//    Drafts print with a DRAFT mark using the LIVE on-screen state.
//    Export-to-PDF opens the same document with a hidden-in-print hint to
//    choose "Save as PDF" and a title that gives a clean PDF filename.
//  - v4: EMAILS SECTION — the final tab is now "Emails & summary": every
//    email sent about this appraisal (meeting invitation + pre-appraisal
//    questionnaire, logged verbatim by /api/appraisals/book on the
//    skeleton's emailsLog) is listed with expandable full text, above the
//    summary snapshot. Booking happens from the staff grid's "Book
//    appraisal" button (Staff.js v4).
//  - v3.3: FOCUS FIX — Field/PreAnswer/PreRatings hoisted to module scope
//    (defining them inside the component remounted the input on every
//    keystroke, dropping focus after one character). SUMMARY PREVIEW &
//    SNAPSHOT — "Preview & send summary" opens the exact email text in an
//    editable modal before anything is sent; the sent text is stored
//    verbatim on the record (sentSummary) and shown on a new final
//    "Summary sent" tab. Re-sending seeds the editor with the last-sent
//    text and replaces the snapshot. Requires staff_appraisals_routes.js
//    v3.3 (/summary-preview; send-summary accepts edited text).
//  - v3.2: INLINE SELF-RATINGS — the relevant 1–5 pre-appraisal ratings
//    appear as pills above the matching appraiser box (patient care /
//    communication / teamwork above "went well"; accuracy / SOPs /
//    medicines / pressure above "areas to develop"; learning /
//    safeguarding / confidentiality above the training plan; punctuality /
//    professionalism above "areas of concern"). Self-scores of 1–2 are
//    highlighted amber as self-identified development areas. NEAR-FULLSCREEN
//    modal (96vw × 94vh) that can no longer be dismissed by clicking the
//    backdrop; Close warns if there are unsaved changes. TAB COMPLETION:
//    each sub-tab turns green with a ✓ once its section has content.
//  - INLINE PRE-APPRAISAL ANSWERS (v3.1): each questionnaire answer is
//    ALSO shown as a read-only indigo callout directly above the matching
//    appraiser box on the relevant tab — agenda items on Narrative; went
//    well / proud of / challenges on Performance; training wishes on
//    Training; workload+wellbeing, career, and ideas+safety on Wellbeing
//    & general. Callouts only render when the question was answered.
//  - PRE-APPRAISAL tab (read-only, right of the AI analysis tab): shows
//    the staff member's answers to the pre-appraisal questionnaire —
//    autosaved into <tenant>.staff_preappraisals as they type on the
//    public tokenised form — plus their 1–5 self-ratings. A send/re-send
//    button lives here and on the Staff grid (left of "Appraisal").
//
// Complete locks the record server-side and snapshots the automated pack;
// then a summary can be emailed to the staff member (platform address).
import API_BASE from "./apiBase";

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useMaskNames, displayName } from "./nameMask";
import ReviewPackView from "./ReviewPack";

const API = `${API_BASE}`;
const auth = () => ({ "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || localStorage.getItem("authToken")}` });
const post = async (path, body) => {
  const r = await fetch(`${API}${path}`, { method: "POST", headers: auth(), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d;
};

const SUBTABS = ["Narrative & evidence", "Pre-appraisal", "Performance", "Training & development", "Objectives", "Wellbeing & general", "Sign-off", "Follow-ups", "Emails & summary"];
// SOURCE SCHEME (cascaded from the embedded appraisals page). READ-ONLY
// content is still identified by a pale FILL, and the fill marks OWNERSHIP
// rather than editability, so a completed (locked) record keeps it:
//   amber (italic) = the staff member's own words from their pre-appraisal
//   pale blue      = auto-generated from the year's data
// v7: the manager's own fields no longer have a fill at all — they are
// white, and signal answered / outstanding through the BORDER instead
// (FIELD_EMPTY / FIELD_FILLED below). The old pale-green manager fill made
// the whole form read as one green wash.
const TINT = {
  // v7: mgrBg/mgrBorder are RETIRED — the manager's fields are white now
  // and signal their state through the border (see FIELD_* below). Kept
  // only so any other module importing TINT doesn't break.
  mgrBg: "#DCFCE7", mgrBorder: "#86EFAC",
  staffBg: "#FEF3C7", staffPanel: "#FFFBEB", staffBorder: "#FCD34D", staffText: "#92400E",
  autoBg: "#DBEAFE", autoPanel: "#EFF6FF", autoBorder: "#93C5FD", autoText: "#1E40AF"
};
const capStyle = (kind) => ({
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase",
  padding: "4px 9px", borderRadius: 6, display: "inline-block", margin: "6px 0 8px",
  background: kind === "staff" ? TINT.staffBg : TINT.autoBg,
  color: kind === "staff" ? TINT.staffText : TINT.autoText,
  border: `1px solid ${kind === "staff" ? TINT.staffBorder : TINT.autoBorder}`
});
/* ======================================================================
   v7: WHITE FIELDS WITH SIGNAL BORDERS
   ----------------------------------------------------------------------
   The pale-green fill on every manager field (v5) made the form read as
   one solid green wash. The fill is gone — all manager inputs are now
   WHITE — and the state moves into the BORDER instead:

     2px solid black  → not yet typed in / nothing selected  (ATTENTION)
     2px solid green  → answered
     2px solid grey   → record locked (completed year) — no call to action

   Padding drops 1px on each axis so the wider border doesn't change the
   overall size of any field or shift the table columns.
   ====================================================================== */
const FIELD_EMPTY  = "#111827";   // near-black — outstanding, draws the eye
const FIELD_FILLED = "#16A34A";   // green — done
const FIELD_LOCKED = "#D1D5DB";   // grey — locked record, nothing to do
const FIELD_MUTED  = "#CBD5E1";   // quiet border for unchosen options once a choice is made

// Treats "", null, undefined and whitespace as empty; an empty array is
// empty too, so multi-select style groups can share the same test.
const filledVal = (v) => Array.isArray(v)
  ? v.length > 0
  : v !== null && v !== undefined && String(v).trim().length > 0;

const fieldBorder = (v, locked) =>
  `2px solid ${locked ? FIELD_LOCKED : filledVal(v) ? FIELD_FILLED : FIELD_EMPTY}`;

const inpBase = { fontSize: 13, padding: "5px 9px", background: "#ffffff", borderRadius: 4, width: "100%", boxSizing: "border-box" };
// inp(value, locked) — the style for a single-line input / select / date.
const inp = (v, locked) => ({ ...inpBase, border: fieldBorder(v, locked) });
// ta(value, locked) — the same, as a textarea.
const ta = (v, locked) => ({ ...inp(v, locked), minHeight: 84, fontFamily: "inherit" });
// inpOpt(value) — for fields explicitly marked optional (e.g. the staff
// member's own signature). Green once filled, but quiet grey when empty:
// a black attention border on something nobody has to complete would send
// the manager chasing a box that isn't blocking anything.
const inpOpt = (v) => ({ ...inpBase, border: `2px solid ${filledVal(v) ? FIELD_FILLED : FIELD_MUTED}` });
const lab = { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3, margin: "12px 0 4px" };
const btn = (c) => ({ border: `1px solid ${c}`, color: c, background: "#fff", borderRadius: 4, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" });
const smallTh = { fontSize: 11, fontWeight: 700, color: "#6b7280", textAlign: "left", padding: "4px 6px" };

// FOCUS FIX (v3.3): these were previously defined INSIDE the component, so
// every keystroke re-created the component type and React remounted the
// input — dropping focus after one character. Module scope keeps the
// element type stable so inputs stay mounted while you type.
function Field({ label, path, area = true, ph, sections, set, locked }) {
  const value = path.split(".").reduce((o, k) => o?.[k], sections) || "";
  return (
    <div><div style={lab}>{label}</div>
      {area
        ? <textarea style={ta(value, locked)} disabled={locked} value={value} onChange={e => set(path, e.target.value)} placeholder={ph} />
        : <input style={inp(value, locked)} disabled={locked} value={value} onChange={e => set(path, e.target.value)} placeholder={ph} />}
    </div>
  );
}

function PreAnswer({ pre, ids, label }) {
  if (!pre) return null;
  const answered = (pre.questions || []).filter(
    q => ids.includes(q.id) && String(pre.answers?.[q.id] || "").trim());
  if (!answered.length) return null;
  return (
    <div style={{ background: TINT.staffBg, border: `1px solid ${TINT.staffBorder}`, borderRadius: 6, padding: "6px 10px", margin: "10px 0 -6px", fontSize: 12.5 }}>
      <div style={{ fontWeight: 700, color: TINT.staffText, marginBottom: 2 }}>
        💬 {label || "From their pre-appraisal"}
      </div>
      {answered.map(q => (
        <div key={q.id} style={{ marginBottom: 4 }}>
          {answered.length > 1 && <div style={{ fontWeight: 600, color: TINT.staffText, fontSize: 11.5 }}>{q.q}</div>}
          <div style={{ whiteSpace: "pre-wrap", color: "#1f2937", fontStyle: "italic" }}>{pre.answers[q.id]}</div>
        </div>
      ))}
    </div>
  );
}

function PreRatings({ pre, ids, label }) {
  if (!pre) return null;
  const items = (pre.ratingItems || []).filter(r => ids.includes(r.id) && pre.ratings?.[r.id]);
  if (!items.length) return null;
  return (
    <div style={{ background: TINT.staffBg, border: `1px solid ${TINT.staffBorder}`, borderRadius: 6, padding: "6px 10px", margin: "10px 0 -6px", fontSize: 12.5 }}>
      <div style={{ fontWeight: 700, color: TINT.staffText, marginBottom: 4 }}>📊 {label || "Their self-ratings (pre-appraisal)"}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {items.map(r => {
          const v = pre.ratings[r.id];
          const low = v <= 2;
          return (
            <span key={r.id} title={`${r.label}: ${v}/5 (self-rated)${low ? " — they've flagged this as an area to develop" : ""}`}
              style={{ border: `1px solid ${low ? "#f59e0b" : TINT.staffBorder}`, background: low ? "#fde68a" : "#fff",
                       color: TINT.staffText, fontStyle: "italic", borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>
              {r.label.split(" (")[0]} <span style={{ letterSpacing: 1 }}>{"●".repeat(v)}{"○".repeat(5 - v)}</span> <b>{v}/5</b>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// 0–5 overall performance scale, lifted from the Appraisal & Development
// Planning form. Shown next to the selector so the definition is always in
// front of the assessor.
/* ======================================================================
   v6: SIMPLE APPRAISAL RECORDS  (<tenant>.staff_appraisals)
   ----------------------------------------------------------------------
   The older/standalone appraisal tool writes a flat record — review type
   and dates, eight enum ratings, free-text blocks, a next-review date.
   It has no sections, no draft/complete lifecycle and no evidence pack,
   so it gets ONE read-only tab rather than the nine-sub-tab machinery.
   Teal throughout marks "this came from the simple tool", matching the
   teal date tinting on the staff grid.
   ====================================================================== */

const SIMPLE_TEAL = { bg: "#eff6ff", border: "#93c5fd", text: "#1976d2", deep: "#115e59" };

// Enum → pill colour. Unknown values fall through to neutral rather than
// guessing, so a new enum from the simple tool never renders misleadingly
// (e.g. showing an unrecognised poor rating in green).
const SIMPLE_RATING_COLOURS = {
  OUTSTANDING:           { bg: "#dcfce7", br: "#4ade80", fg: "#14532d" },
  EXCEEDS_EXPECTATIONS:  { bg: "#dcfce7", br: "#86efac", fg: "#166534" },
  MEETS_EXPECTATIONS:    { bg: "#e0f2fe", br: "#7dd3fc", fg: "#075985" },
  SATISFACTORY:          { bg: "#e0f2fe", br: "#7dd3fc", fg: "#075985" },
  DEVELOPING:            { bg: "#fef3c7", br: "#fcd34d", fg: "#92400e" },
  NEEDS_IMPROVEMENT:     { bg: "#fee2e2", br: "#fca5a5", fg: "#991b1b" },
  UNSATISFACTORY:        { bg: "#fee2e2", br: "#fca5a5", fg: "#991b1b" }
};
const simpleRatingStyle = (v) => SIMPLE_RATING_COLOURS[String(v || "").toUpperCase()]
  || { bg: "#f3f4f6", br: "#d1d5db", fg: "#374151" };

const fmtDMYLong = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "—"
    : t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// Short label for the tab button itself.
const simpleTabLabel = (r) => {
  const d = r.reviewDate ? new Date(r.reviewDate) : null;
  const when = d && !isNaN(d.getTime())
    ? d.toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "undated";
  const kind = r.reviewType ? String(r.reviewType).replace(/_/g, " ").toLowerCase() : "review";
  return `${when} · ${kind}`;
};

function SimpleAppraisalView({ rec, staffShown, roleName, pharmacyShown }) {
  const box = {
    border: `1px solid ${SIMPLE_TEAL.border}`, background: SIMPLE_TEAL.bg,
    borderRadius: 6, padding: "10px 12px"
  };
  const metaCell = (label, value) => (
    <div key={label}>
      <div style={{ ...lab, margin: "0 0 2px" }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "#111827" }}>{value}</div>
    </div>
  );

  return (
    <div>
      <div style={{ ...box, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <b style={{ color: SIMPLE_TEAL.deep, fontSize: 14 }}>Quick appraisal record</b>
        <span style={{ fontSize: 12.5, color: "#4b5563" }}>
          Recorded with the simple appraisal tool — read-only here. There is no
          pre-appraisal questionnaire, evidence pack or sign-off workflow behind
          this record. Use a full appraisal for the next cycle if you need those.
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 4 }}>
        {metaCell("Review type", rec.reviewType ? String(rec.reviewType).replace(/_/g, " ") : "—")}
        {metaCell("Review date", fmtDMYLong(rec.reviewDate))}
        {metaCell("Period covered", `${fmtDMYLong(rec.periodFrom)} → ${fmtDMYLong(rec.periodTo)}`)}
        {metaCell("Next review due", fmtDMYLong(rec.nextReviewDate))}
        {metaCell("Reviewed by", rec.reviewedByName || "—")}
        {metaCell("Employee agreed", rec.employeeReviewedAndAgreed ? "✓ Yes" : "Not recorded")}
      </div>

      {rec.overallRatingLabel && (() => {
        const c = simpleRatingStyle(rec.overallRating);
        return (
          <div style={{ marginTop: 14 }}>
            <div style={lab}>Overall rating</div>
            <span style={{ display: "inline-block", background: c.bg, border: `1px solid ${c.br}`, color: c.fg,
                           borderRadius: 999, padding: "5px 16px", fontSize: 14, fontWeight: 800 }}>
              {rec.overallRatingLabel}
            </span>
          </div>
        );
      })()}

      {rec.ratings?.length > 0 && (
        <>
          <div style={lab}>Performance ratings</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 8 }}>
            {rec.ratings.map(r => {
              const c = simpleRatingStyle(r.value);
              return (
                <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                                          border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px" }}>
                  <span style={{ fontSize: 13, color: "#374151" }}>{r.label}</span>
                  <span style={{ background: c.bg, border: `1px solid ${c.br}`, color: c.fg, borderRadius: 999,
                                 padding: "2px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {r.valueLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {rec.texts?.map(t => (
        <div key={t.key}>
          <div style={lab}>{t.label}</div>
          <div style={{ ...box, fontSize: 13.5, lineHeight: 1.5, whiteSpace: "pre-wrap", color: "#111827" }}>{t.value}</div>
        </div>
      ))}

      {!rec.ratings?.length && !rec.texts?.length && (
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 16 }}>
          This record was saved with dates only — no ratings or written comments were entered.
        </div>
      )}

      <div style={{ marginTop: 18, fontSize: 11.5, color: "#9ca3af" }}>
        Record {rec._id} · created {fmtDMYLong(rec.createdAt)}
        {rec.updatedAt && String(rec.updatedAt) !== String(rec.createdAt) ? ` · last edited ${fmtDMYLong(rec.updatedAt)}` : ""}
        {` · ${staffShown}${roleName ? ` (${roleName})` : ""} · ${pharmacyShown}`}
      </div>
    </div>
  );
}

const RATING_SCALE = [
  [5, "Outstanding", "Makes significant impact beyond their immediate sphere of responsibility. Achieves or exceeds all objectives."],
  [4, "Excellent", "Performs the job at a level which exceeds expectations. Achieves or exceeds all objectives."],
  [3, "Fully effective", "Performs at a level which meets expectations and the requirements of the job. Achieves or exceeds most objectives."],
  [2, "Approaching expectations", "Achieves some objectives. Performance requires some improvement to reach the fully effective level."],
  [1, "Under-performing", "Performs at a level which requires significant improvement. Does not achieve objectives."],
  [0, "Not applicable", "Insufficient time in position to properly assess."]
];

const emptySections = () => ({
  reviewType: "annual",                 // 'annual' | 'probation'
  reviewPeriodFrom: "", reviewPeriodTo: "",
  narrativeComment: "",
  overallRating: null,                  // 0–5 from RATING_SCALE
  ratingSummary: "",
  performance: { wentWell: "", develop: "", achievements: "" },
  lastObjectives: [],                   // { title, measure, result, comments }
  objectives: [],                       // { title, measure, support, targetDate, reviewNotes }
  training: { cpdDone: "", needsSummary: "", revalidationNote: "" },
  trainingPlan: [],                     // { need, aim, method, support, cost, timescale }
  wellbeing: { workload: "", aspirations: "", potential: "", supportAgreed: "" },
  general: { concerns: "", suggestions: "", further: "" },
  staffComments: "",
  followUps: []
});

export default function StaffAppraisal({ pharmacyName, staffId, staffName, roleName, onClose }) {
  const masked = useMaskNames();
  const thisYear = new Date().getUTCFullYear();
  const [appraisals, setAppraisals] = useState([]);
  const [yearTab, setYearTab] = useState(thisYear);
  const [sub, setSub] = useState(0);
  const [sections, setSections] = useState(emptySections());
  const [meetingDate, setMeetingDate] = useState("");
  const [pack, setPack] = useState(null);            // live/snapshot review pack for narrative tab
  const [packLoading, setPackLoading] = useState(false); // computing indicator for draft years
  const [pre, setPre] = useState(null);              // pre-appraisal doc (read-only tab)
  const [preLoading, setPreLoading] = useState(false);
  const [busy, setBusy] = useState(null);            // 'save' | 'complete' | 'send' | 'presend'
  const [err, setErr] = useState(null);
  const [signAppraiser, setSignAppraiser] = useState("");
  const [signStaff, setSignStaff] = useState("");
  const [savedSnap, setSavedSnap] = useState("");   // v3.2: unsaved-change detection
  const [summaryModal, setSummaryModal] = useState(null); // v3.3: { to, subject, text, previouslySentAt }
  // v6: read-only records from the SIMPLE appraisal tool
  // (<tenant>.staff_appraisals). One extra tab each, teal-marked.
  const [simpleList, setSimpleList] = useState([]);
  const [simpleTab, setSimpleTab] = useState(null);  // _id of the open simple record, or null
  // v6.1: the dialogue picks its opening tab ONCE per open. Without this
  // guard every save/reload would yank the user back to the auto-picked
  // tab mid-edit.
  const landedRef = useRef(false);

  const current = appraisals.find(a => a.appraisalYear === yearTab) || null;
  const locked = current?.status === "completed";
  // When a simple record's tab is open the whole editing apparatus is
  // hidden — there is nothing on it to save, complete, email or snapshot.
  const activeSimple = simpleTab ? (simpleList.find(x => x._id === simpleTab) || null) : null;

  const loadPre = useCallback(async () => {
    setPreLoading(true);
    try {
      const d = await post("/api/appraisals/pre/get", { pharmacyName, staffId, appraisalYear: yearTab });
      setPre(d.pre || null);
    } catch (_) { setPre(null); }
    finally { setPreLoading(false); }
  }, [pharmacyName, staffId, yearTab]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await post("/api/appraisals/list", { pharmacyName, staffId });
      setAppraisals(d.appraisals || []);
      setSimpleList(d.simpleAppraisals || []);

      // v6.1: OPEN ON WHAT ACTUALLY EXISTS. Booking an appraisal or
      // sending a pre-appraisal creates an empty skeleton for the current
      // year, so defaulting to the year tab landed the user on a blank
      // form while the only real content — a simple record — sat behind a
      // tab they had no reason to notice. If no full appraisal anywhere
      // has content yet, open the most recent simple record instead.
      if (!landedRef.current) {
        landedRef.current = true;
        const simples = d.simpleAppraisals || [];
        const hasRealAdvanced = (d.appraisals || []).some(a =>
          a.status === "completed" || a.meetingDate ||
          Object.values(a.sections || {}).some(v =>
            typeof v === "string" ? v.trim() : Array.isArray(v) ? v.length : !!v));
        if (simples.length && !hasRealAdvanced) setSimpleTab(simples[0]._id);
      }
      const cur = (d.appraisals || []).find(a => a.appraisalYear === yearTab);
      const loadedSections = cur?.sections ? { ...emptySections(), ...cur.sections } : emptySections();
      const loadedDate = cur?.meetingDate ? String(cur.meetingDate).slice(0, 10) : "";
      setSections(loadedSections);
      setMeetingDate(loadedDate);
      setSavedSnap(JSON.stringify({ s: loadedSections, d: loadedDate }));
      // Narrative source: snapshot if completed, else the live pack
      if (cur?.status === "completed" && cur.reviewSnapshot) setPack(cur.reviewSnapshot);
      else {
        // Draft (uncompleted) years: automatically PERFORM the analysis when
        // the dialogue opens — the refresh endpoint recomputes the 12-month
        // pack fresh and replaces the stored doc (same as the review popup).
        setPackLoading(true);
        try {
          const p = await post("/staff_annual_review_refresh", { pharmacyName, staffId, reviewYear: yearTab });
          setPack(p.review || null);
        } catch (_) { setPack(null); }
        finally { setPackLoading(false); }
      }
      loadPre();   // the read-only pre-appraisal tab (never blocks the rest)
    } catch (e) { setErr(e.message); }
  }, [pharmacyName, staffId, yearTab, loadPre]);

  useEffect(() => { load(); }, [load]);

  const saveDraft = async () => {
    setBusy("save"); setErr(null);
    try {
      await post("/api/appraisals/save", {
        pharmacyName, staffId, appraisalYear: yearTab, sections, meetingDate,
        staffName, roleName,
        appraiser: { name: localStorage.getItem("userName") || "", roleKey: localStorage.getItem("roleKey") || "" }
      });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const complete = async () => {
    if (!signAppraiser.trim()) { setErr("Appraiser sign-off (typed name) required"); return; }
    setBusy("complete"); setErr(null);
    try {
      await saveDraft();
      await post("/api/appraisals/complete", {
        pharmacyName, staffId, appraisalYear: yearTab,
        appraiser: { name: signAppraiser.trim(), roleKey: localStorage.getItem("roleKey") || "MANAGER" },
        signatures: { appraiser: { name: signAppraiser.trim(), at: new Date().toISOString() },
                      staff: signStaff.trim() ? { name: signStaff.trim(), at: new Date().toISOString() } : null }
      });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  // v3.3: the summary is PREVIEWED and EDITABLE before it goes anywhere.
  // Opening the preview fetches the default text (or, on a re-send, the
  // text sent last time); Send posts the edited version and the server
  // snapshots exactly what was sent onto the record ("Summary sent" tab).
  const openSummaryPreview = async () => {
    setBusy("send"); setErr(null);
    try {
      const r = await post("/api/appraisals/summary-preview", { pharmacyName, staffId, appraisalYear: yearTab });
      setSummaryModal(r);
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const sendSummary = async () => {
    if (!summaryModal || !String(summaryModal.text || "").trim()) { setErr("Summary text is empty"); return; }
    setBusy("send"); setErr(null);
    try {
      const r = await post("/api/appraisals/send-summary", { pharmacyName, staffId, appraisalYear: yearTab, text: summaryModal.text });
      setSummaryModal(null);
      window.alert(`Summary sent to ${r.sentTo}. The exact text sent is stored on the "Summary sent" tab.`);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  // v5: email the conducting manager a tokenised APPRAISER FORM — the
  // whole appraisal completable from that link, no system login. Autosaves
  // into the same record this dialogue edits; completion locks it and
  // kills the link. Authority is stamped from THIS user's role at issue.
  const sendAppraiserForm = async () => {
    const to = window.prompt(
      "Email the appraiser form to which address?\n\nThe link lets the whole appraisal be conducted and completed from that email — it expires in 30 days, dies on completion, and edits sync with this dialogue.",
      localStorage.getItem("userEmail") || "");
    if (!to) return;
    setBusy("appform"); setErr(null);
    try {
      const r = await post("/api/appraisals/appraiser-form/send", {
        pharmacyName, staffId, appraisalYear: yearTab, toEmail: to.trim(),
        issuedBy: { name: localStorage.getItem("userName") || "", roleKey: localStorage.getItem("roleKey") || "MANAGER" }
      });
      window.alert(`Appraiser form link emailed to ${r.sentTo} (expires ${new Date(r.expires).toLocaleDateString("en-GB")}). The email is logged on the Emails & summary tab.`);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const sendPre = async () => {
    setBusy("presend"); setErr(null);
    try {
      const r = await post("/api/appraisals/pre/send", { pharmacyName, staffId, appraisalYear: yearTab });
      window.alert(`Pre-appraisal questionnaire ${r.resend ? "re-sent" : "sent"} to ${r.sentTo}. Their answers save automatically as they type and will appear on the Pre-appraisal tab.`);
      await loadPre();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const set = (path, v) => setSections(s => {
    const c = JSON.parse(JSON.stringify(s)); const parts = path.split(".");
    let o = c; while (parts.length > 1) o = o[parts.shift()];
    o[parts[0]] = v; return c;
  });

  // v6 FIX: filter to real integers. Before the collection split, simple
  // records came back through this list with no appraisalYear, producing
  // a dead "undefined" tab that opened an unsaveable blank form.
  const years = [...new Set([thisYear, ...appraisals.map(a => a.appraisalYear)])]
    .filter(y => Number.isInteger(y))
    .sort((a, b) => b - a);

  /* ---- v4.1: PRINT / EXPORT the ENTIRE appraisal form -------------------
     Builds one self-contained HTML document covering EVERY section — the
     header block (pharmacy, staff, role, review type/period, meeting date,
     status), the appraiser's evidence commentary, the pre-appraisal
     answers and self-ratings, performance rating + summaries, training
     plan, both objectives tables, wellbeing & general, sign-off,
     follow-ups, the emails log and the sent-summary snapshot. Uses the
     LIVE section state, so unsaved drafts print exactly as on screen
     (marked DRAFT). "Print" opens it with the print dialogue; "Export to
     PDF" is the same document with the title set for a clean PDF filename
     and an on-screen hint (hidden in print) to choose "Save as PDF". */
  const buildFullAppraisalHtml = () => {
    const escH = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const nl = (x) => escH(x).replace(/\n/g, "<br/>");
    const val = (x) => (String(x || "").trim() ? nl(x) : "<span class='empty'>—</span>");
    const dmy = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
    const s = sections;
    const staffShown = displayName(staffName, masked);
    const pharmShown = displayName(pharmacyName, masked);
    const ratingDef = RATING_SCALE.find(r => r[0] === s.overallRating);

    const tbl = (headers, rows) => `
      <table><tr>${headers.map(h => `<th>${escH(h)}</th>`).join("")}</tr>
      ${rows.length ? rows.map(r => `<tr>${r.map(c => `<td>${val(c)}</td>`).join("")}</tr>`).join("") : `<tr><td colspan="${headers.length}" class="empty">None recorded</td></tr>`}
      </table>`;
    const block = (title, inner) => `<div class="sec"><h2>${escH(title)}</h2>${inner}</div>`;
    const field = (label, v) => `<div class="fld"><div class="flab">${escH(label)}</div><div class="fval">${val(v)}</div></div>`;

    const preHtml = !pre ? `<div class="empty">No pre-appraisal sent for ${yearTab}.</div>` : `
      <div class="meta">Sent to ${escH(pre.sentTo || "—")} ${pre.sentAt ? "on " + dmy(pre.sentAt) : ""} ·
        ${pre.status === "responded" ? "submitted " + (pre.respondedAt ? new Date(pre.respondedAt).toLocaleString("en-GB") : "") : "awaiting submission"}</div>
      <table><tr><th>Self-rating (1 = area to develop · 5 = strength)</th><th style="width:90px">Score</th></tr>
        ${(pre.ratingItems || []).map(r => `<tr><td>${escH(r.label)}</td><td>${pre.ratings?.[r.id] ? pre.ratings[r.id] + " / 5" : "<span class='empty'>—</span>"}</td></tr>`).join("")}
      </table>
      ${(pre.questions || []).map(q => field(q.q, pre.answers?.[q.id])).join("")}`;

    return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Appraisal_${escH(staffShown).replace(/\s+/g, "_")}_${yearTab}</title>
<style>
  body{font-family:'Segoe UI',Calibri,Arial,sans-serif;font-size:12.5px;color:#111;margin:28px 34px;}
  h1{font-size:20px;margin:0 0 2px;color:#1976d2;}
  h2{font-size:14px;margin:0 0 8px;color:#1976d2;border-bottom:2px solid #1976d2;padding-bottom:3px;}
  .head{border:1px solid #cbd5e1;border-radius:6px;padding:12px 16px;margin:12px 0 18px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px 20px;}
  .head b{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.4px;color:#6b7280;}
  .sec{margin-bottom:18px;page-break-inside:avoid;}
  .fld{margin:8px 0;} .flab{font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.3px;color:#374151;}
  .fval{border:1px solid #e5e7eb;border-radius:4px;padding:6px 9px;margin-top:2px;background:#fafafa;white-space:pre-wrap;}
  table{border-collapse:collapse;width:100%;margin:6px 0;} th,td{border:1px solid #9ca3af;padding:5px 8px;text-align:left;font-size:12px;vertical-align:top;}
  th{background:#f3f4f6;font-size:10.5px;text-transform:uppercase;letter-spacing:.3px;}
  .empty{color:#9ca3af;} .meta{font-size:11.5px;color:#374151;margin-bottom:6px;}
  .draft{color:#b91c1c;border:2px solid #b91c1c;display:inline-block;padding:2px 10px;border-radius:4px;font-weight:800;margin-left:10px;}
  .pdfhint{background:#eef2ff;border:1px solid #c7d2fe;border-radius:6px;padding:8px 12px;margin-bottom:14px;font-size:13px;color:#3730a3;}
  @media print{.pdfhint{display:none;} body{margin:6mm 8mm;}}
</style></head><body>
<div class="pdfhint" id="pdfhint" style="display:none;">To save this appraisal as a PDF: in the print dialogue that opens, set the destination/printer to <b>"Save as PDF"</b>, then Save. This note will not appear in the document.</div>
<h1>Staff Appraisal — ${yearTab}${locked ? "" : `<span class="draft">DRAFT</span>`}</h1>
<div class="head">
  <div><b>Staff member</b>${escH(staffShown)}</div>
  <div><b>Role</b>${escH(roleName || "—")}</div>
  <div><b>Pharmacy</b>${escH(pharmShown)}</div>
  <div><b>Review type</b>${s.reviewType === "probation" ? "End of probation" : "Annual review"}</div>
  <div><b>Review period</b>${dmy(s.reviewPeriodFrom)} – ${dmy(s.reviewPeriodTo)}</div>
  <div><b>Meeting date</b>${dmy(meetingDate)}</div>
  <div><b>Status</b>${locked ? "Completed " + dmy(current?.completedAt) : "Draft"}</div>
  <div><b>Appraiser</b>${escH(current?.appraiser?.name || current?.signatures?.appraiser?.name || localStorage.getItem("userName") || "—")}</div>
  <div><b>Printed</b>${new Date().toLocaleString("en-GB")}</div>
</div>
${block("1. Evidence & appraiser commentary",
  (pack ? (
    `<div class="meta">12-month snapshot: window ${dmy(pack.windowStart)} – ${dmy(pack.windowEnd)}, computed ${dmy(pack.computedAt)}${pack.cohort ? ` · cohort ${escH(pack.cohort.type === "role" ? `${pack.cohort.roleName || "role-matched"} (${pack.cohort.size})` : `whole pharmacy (${pack.cohort.size ?? "—"})`)}` : ""}${locked ? " · snapshotted at completion" : " · live at time of printing"}</div>` +
    field("Narrative summary (automated)", pack.narrative) +
    ((pack.insights || []).length
      ? `<div class="flab" style="margin-top:8px;">Insights (${pack.insights.length})</div>` +
        pack.insights.map(i => `<div class="fval" style="border-left:4px solid ${i.polarity === "positive" ? "#16a34a" : i.polarity === "negative" ? "#dc2626" : "#9ca3af"};margin-top:4px;"><b>${escH(i.id)} · ${escH(i.title)} — severity ${escH(String(i.severity))}</b><br/>${nl(i.detail)}</div>`).join("")
      : `<div class="meta">No statistical outliers identified this year.</div>`) +
    `<div class="flab" style="margin-top:8px;">Key figures</div><div class="fval">` +
    [
      pack.nearMisses?.asDispenser ? `Near misses as dispenser: <b>${pack.nearMisses.asDispenser.value ?? "—"}</b>${pack.nearMisses.asDispenser.ratio != null ? ` (${pack.nearMisses.asDispenser.ratio}× cohort)` : ""}` : null,
      pack.nearMisses?.asChecker ? `errors caught as checker: <b>${pack.nearMisses.asChecker.value ?? "—"}</b>` : null,
      pack.medicationIncidents ? `medication incidents: <b>${pack.medicationIncidents.involvedCount ?? 0}</b>` : null,
      pack.attendance?.hasData ? `attendance: <b>${pack.attendance.shiftsWorked}</b> shifts, <b>${pack.attendance.lateCount}</b> late starts, <b>${pack.attendance.earlyCount}</b> early departures` : null,
      pack.sops && pack.sops.totalCards > 0 ? `SOPs: <b>${pack.sops.read}</b> of <b>${pack.sops.totalCards}</b> read (${pack.sops.unread} unread)` : null,
      pack.comparison?.hasPriorYear ? `vs ${pack.comparison.priorReviewYear}: near misses ${pack.comparison.priorNearMissesAsDispenser} → ${pack.nearMisses?.asDispenser?.value ?? "—"}` : "baseline year (no prior review)"
    ].filter(Boolean).join(" · ") + `</div>`
  ) : `<div class="meta">No 12-month analysis computed for ${yearTab} — the evidence pack section is empty.</div>`) +
  field("Appraiser commentary on the evidence", s.narrativeComment))}
${block("2. Pre-appraisal — the staff member's own submission", preHtml)}
${block("3. Overall assessment of performance",
  `<div class="fld"><div class="flab">Rating</div><div class="fval">${ratingDef ? `<b>${ratingDef[0]} — ${escH(ratingDef[1])}</b>: ${escH(ratingDef[2])}` : "<span class='empty'>not set</span>"}</div></div>` +
  field("Summary of overall performance", s.ratingSummary) +
  field("What went well this year", s.performance?.wentWell) +
  field("Achievements", s.performance?.achievements) +
  field("Areas to develop", s.performance?.develop))}
${block("4. Training & development",
  field("CPD / training completed this year", s.training?.cpdDone) +
  tbl(["Development need", "Aim", "Method", "Support required", "Est. cost", "Timescale"],
    (s.trainingPlan || []).map(t => [t.need, t.aim, t.method, t.support, t.cost, t.timescale])) +
  field("Training needs — commentary", s.training?.needsSummary) +
  field("Revalidation support", s.training?.revalidationNote))}
${block("5. Objectives from the last review — results achieved",
  tbl(["Objective", "How measured", "Results achieved", "Comments"],
    (s.lastObjectives || []).map(o => [o.title, o.measure, o.result, o.comments])))}
${block("6. SMART objectives for the coming year",
  tbl(["Objective", "How measured", "Support required", "Deadline", "Review notes & date"],
    (s.objectives || []).map(o => [o.title, o.measure, o.support, o.targetDate, o.reviewNotes])))}
${block("7. Wellbeing & general",
  field("Workload & wellbeing discussion", s.wellbeing?.workload) +
  field("Career aspirations", s.wellbeing?.aspirations) +
  field("Potential — appraiser's comment", s.wellbeing?.potential) +
  field("Support agreed", s.wellbeing?.supportAgreed) +
  field("Areas of concern (action & owner)", s.general?.concerns) +
  field("Suggestions for improvement", s.general?.suggestions) +
  field("Any further comments", s.general?.further))}
${block("8. Follow-up actions",
  tbl(["Done", "Action", "Owner", "Due"],
    (s.followUps || []).map(f => [f.done ? "✓" : "", f.action, f.owner, f.dueDate])))}
${block("9. Sign-off",
  field("Staff member's own comments", s.staffComments) +
  `<div class="meta">${locked
    ? `Signed: <b>${escH(current?.signatures?.appraiser?.name || "—")}</b> (appraiser)` +
      (current?.signatures?.staff?.name ? `; <b>${escH(current.signatures.staff.name)}</b> (staff member)` : "") +
      ` — ${new Date(current.completedAt).toLocaleString("en-GB")}`
    : "Not yet completed — signatures are captured when the appraisal is completed on the system."}</div>`)}
${block("10. Emails & summary",
  tbl(["Type", "To", "Sent", "Subject"],
    (current?.emailsLog || []).map(m => [
      m.type === "pre_appraisal" ? "Pre-appraisal form" : m.type === "appraisal_invite" ? "Meeting invitation" : m.type === "appraiser_form" ? "Appraiser form link" : m.type,
      m.to, m.sentAt ? new Date(m.sentAt).toLocaleString("en-GB") : "", m.subject])) +
  (current?.sentSummary
    ? field(`Summary emailed to the staff member (${current.sentSummary.to}, ${new Date(current.sentSummary.sentAt).toLocaleString("en-GB")}) — verbatim`, current.sentSummary.text)
    : `<div class="empty">No summary email sent yet.</div>`))}
</body></html>`;
  };

  const openPrintable = (pdfMode) => {
    const w = window.open("", "_blank");
    if (!w) { window.alert("Your browser blocked the print window — allow pop-ups for this site and try again."); return; }
    w.document.write(buildFullAppraisalHtml());
    w.document.close();
    if (pdfMode) { try { w.document.getElementById("pdfhint").style.display = "block"; } catch (_) {} }
    setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 450);
  };

  // v3.2: unsaved-change detection — the modal can no longer be dismissed
  // by clicking the backdrop, and Close warns before losing edits.
  // (Re-instated in v3.3.1 — the v3.3 component-hoisting edit removed it
  // by accident, breaking the build: 'dirty' / 'guardedClose' undefined.)
  const dirty = !locked && JSON.stringify({ s: sections, d: meetingDate }) !== savedSnap;
  const guardedClose = () => {
    if (dirty && !window.confirm("You have unsaved changes on this appraisal.\n\nClose without saving?")) return;
    onClose();
  };

  // v3.2: per-tab completion — a tab turns GREEN with a ✓ once its section
  // has meaningful content (heuristics; Sign-off = completed record).
  const has = (v) => String(v || "").trim().length > 0;

  /* v7: PER-TAB PROGRESS — tabProgress(i) returns [done, total] over the
     SAME criteria tabComplete used before, just counted individually so
     the sub-tab bar can show a part-filled progress bar instead of only
     green/not-green. tabComplete is now derived from it, so the bar
     reaching 100% and the tab showing ✓ can never disagree.

     Where the old rule was an OR (any one of several things counts, e.g.
     Training), the tab is a single step — that bar is empty or full, which
     is honest: there is nothing part-done to report. Where the old rule
     was an AND, each clause is its own step. */
  const tabProgress = (i) => {
    const s = sections;
    const count = (...flags) => [flags.filter(Boolean).length, flags.length];
    switch (i) {
      case 0: return count(has(s.narrativeComment), has(meetingDate));
      // Sent counts as half — chasing a response is different from not
      // having asked yet, and the manager can see which at a glance.
      case 1: return count(!!pre, pre?.status === "responded");
      case 2: return count(
        s.overallRating !== null && s.overallRating !== undefined,
        has(s.performance?.wentWell),
        has(s.performance?.develop));
      case 3: return count(has(s.training?.cpdDone) || has(s.training?.needsSummary) || (s.trainingPlan || []).some(t => has(t.need)));
      case 4: return count((s.objectives || []).some(o => has(o.title)));
      case 5: return count(has(s.wellbeing?.workload), has(s.wellbeing?.aspirations));
      // Sign-off only completes when the record is locked; a typed
      // appraiser name is progress towards that, not the thing itself.
      case 6: return locked ? [1, 1] : [has(signAppraiser) ? 1 : 0, 2];
      case 7: return count((s.followUps || []).some(f => has(f.action)));
      case 8: return count(!!(current?.sentSummary || current?.sentToStaffAt || (current?.emailsLog || []).length));
      default: return [0, 1];
    }
  };
  const tabComplete = (i) => { const [d, t] = tabProgress(i); return t > 0 && d === t; };

  // Seed "objectives from last review" from the previous year's record.
  const seedLastObjectives = () => {
    const prev = appraisals.find(a => a.appraisalYear === yearTab - 1);
    const prevObjectives = prev?.sections?.objectives || [];
    if (!prevObjectives.length) { window.alert(`No objectives found on the ${yearTab - 1} appraisal record.`); return; }
    set("lastObjectives", prevObjectives.map(o => ({
      title: o.title || "", measure: o.measure || "", result: "", comments: ""
    })));
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
      {/* v3.2: NEAR-FULLSCREEN and truly MODAL — no onClick on the backdrop,
          so clicking off the dialogue can't lose work; Close warns when
          there are unsaved changes. Constant size (96vw × 94vh) so tab
          switches never resize the dialogue — each tab scrolls inside the
          same frame. */}
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 8, width: "96vw", height: "94vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div>
            <span style={{ fontWeight: 800, fontSize: 16 }}>Appraisal — {displayName(staffName, masked)}</span>
            <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 8 }}>{roleName} · {displayName(pharmacyName, masked)}</span>
            {activeSimple && (
              <span style={{ marginLeft: 10, background: SIMPLE_TEAL.bg, border: `1px solid ${SIMPLE_TEAL.border}`,
                             color: SIMPLE_TEAL.deep, borderRadius: 999, padding: "3px 10px",
                             fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                Simple record — read-only
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {!activeSimple && !locked && <button style={btn("#1976d2")} disabled={!!busy} onClick={saveDraft}>{busy === "save" ? "Saving…" : "Save draft"}</button>}
            {!activeSimple && locked && !current?.sentToStaffAt && <button style={btn("#4338ca")} disabled={!!busy} onClick={openSummaryPreview}
              title="Shows you exactly what will be emailed and lets you edit it before sending.">{busy === "send" ? "…" : "✉ Preview & send summary"}</button>}
            {!activeSimple && locked && current?.sentToStaffAt && <span style={{ fontSize: 12, color: "#166534" }}>✓ Summary sent {new Date(current.sentToStaffAt).toLocaleDateString("en-GB")}</span>}
            {!activeSimple && !locked && <button style={btn("#7c3aed")} disabled={!!busy} onClick={sendAppraiserForm}
              title="Emails a secure link that opens this whole appraisal as a standalone form — the conducting manager can fill in every section and complete it from that email, no system login. Edits autosave into this same record.">
              {busy === "appform" ? "…" : "✉ Email appraiser form"}</button>}
            {!activeSimple && <button style={btn("#374151")} onClick={() => openPrintable(false)}
              title="Prints the ENTIRE appraisal form — every section, the pre-appraisal answers, sign-off, follow-ups and emails — with a header of pharmacy, staff member, dates and status. Drafts print marked DRAFT.">🖨 Print</button>}
            {!activeSimple && <button style={btn("#374151")} onClick={() => openPrintable(true)}
              title="Opens the same full document ready to save as a PDF — choose 'Save as PDF' as the destination in the print dialogue. The filename defaults to Appraisal_{name}_{year}.">⤓ Export to PDF</button>}
            {activeSimple && <button style={btn("#1976d2")} onClick={() => window.print()}
              title="Prints this quick appraisal record exactly as shown. Simple records have no full appraisal document behind them.">🖨 Print record</button>}
            {!activeSimple && dirty && <span style={{ fontSize: 11, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 999, padding: "3px 10px" }}>Unsaved changes</span>}
            <button style={btn("#6b7280")} onClick={guardedClose}>✕ Close</button>
          </div>
        </div>

        {/* Year tabs — full appraisals (staff_appraisals_advanced), then one
            tab per SIMPLE record (staff_appraisals). A staff member may have
            only full appraisals, only simple ones, or a hybrid of both. */}
        <div style={{ display: "flex", gap: 4, padding: "8px 16px 0", borderBottom: "1px solid #e5e7eb", flexWrap: "wrap", alignItems: "flex-end" }}>
          {years.map(y => {
            const a = appraisals.find(x => x.appraisalYear === y);
            const on = !activeSimple && yearTab === y;
            return (
              <button key={y} onClick={() => { setSimpleTab(null); setYearTab(y); setSub(0); }}
                style={{ border: "none", borderBottom: on ? "3px solid #1976d2" : "3px solid transparent",
                         background: "none", padding: "6px 14px", fontSize: 14, fontWeight: on ? 800 : 600, cursor: "pointer",
                         fontFamily: "inherit", appearance: "none", WebkitAppearance: "none",
                         color: a?.status === "completed" ? "#166534" : on ? "#1976d2" : "#334155" }}>
                {y}{a?.status === "completed" ? " ✓" : a ? " (draft)" : ""}
              </button>
            );
          })}

          {/* v6.1: these read as TABS, not decoration. The first version
              was an unbordered teal label that nobody would think to
              click, so the record it opened was effectively invisible. */}
          {simpleList.length > 0 && (
            <span style={{ alignSelf: "center", marginLeft: 10, marginRight: 2, marginBottom: 8,
                           fontSize: 11, fontWeight: 700, color: SIMPLE_TEAL.text,
                           textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
              Simple records ({simpleList.length}) →
            </span>
          )}
          {simpleList.map(r => {
            const on = activeSimple?._id === r._id;
            return (
              <button key={r._id} onClick={() => setSimpleTab(r._id)}
                title="Open this record from the simple appraisal tool — read-only, with no evidence pack, pre-appraisal or sign-off behind it."
                style={{ border: `1px solid ${SIMPLE_TEAL.border}`,
                         borderBottom: on ? `3px solid ${SIMPLE_TEAL.text}` : `1px solid ${SIMPLE_TEAL.border}`,
                         background: on ? SIMPLE_TEAL.text : SIMPLE_TEAL.bg, padding: "5px 12px", fontSize: 12.5,
                         fontWeight: 700, cursor: "pointer", borderRadius: "6px 6px 0 0", marginBottom: on ? 0 : 2,
                         fontFamily: "inherit", appearance: "none", WebkitAppearance: "none",
                         color: on ? "#ffffff" : SIMPLE_TEAL.deep }}>
                <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                               background: on ? "#ffffff" : SIMPLE_TEAL.text, marginRight: 6, verticalAlign: "middle" }} />
                {simpleTabLabel(r)}
              </button>
            );
          })}
        </div>

        {/* A simple record replaces the entire sub-tab apparatus: there is
            nothing on it to edit, complete, snapshot or email. */}
        {activeSimple ? (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 18px" }}>
            <SimpleAppraisalView
              rec={activeSimple}
              staffShown={displayName(staffName, masked)}
              roleName={roleName}
              pharmacyShown={displayName(pharmacyName, masked)} />
          </div>
        ) : (<>

        {/* Sub tabs */}
        {/* v5: colour-code legend (matches the embedded branch page) */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", padding: "7px 16px 5px", fontSize: 11.5, color: "#4b5563", borderBottom: "1px solid #f1f5f9" }}>
          <b style={{ color: "#2D5A3D" }}>Colour code:</b>
          {/* v7: the manager's fields are white — their STATE is in the
              border, so the legend swatches show borders, not fills. */}
          {[["staff", TINT.staffBg, `1px solid ${TINT.staffBorder}`, "amber — the staff member’s own words (read-only)"],
            ["auto", TINT.autoBg, `1px solid ${TINT.autoBorder}`, "blue — auto-generated from the year’s data (read-only)"],
            ["todo", "#ffffff", `2px solid ${FIELD_EMPTY}`, "black outline — still to complete"],
            ["done", "#ffffff", `2px solid ${FIELD_FILLED}`, "green outline — completed"]].map(([k, bg2, bd, txt]) => (
            <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
              <span style={{ width: 20, height: 12, borderRadius: 3, background: bg2, border: bd, flex: "none" }} />
              {txt}
            </span>
          ))}
        </div>
        {/* v7: each sub-tab carries a PROGRESS BAR beneath its label, filling
            with green as that section's required items are answered and
            showing a solid full-width bar once the section is complete.
            The bar now carries the green signal, so the tab buttons
            themselves stay white (only the active tab is filled) — a green
            pill under a green bar was the same wash we just removed. */}
        <div style={{ display: "flex", gap: 4, padding: "6px 16px 8px", flexWrap: "wrap", alignItems: "flex-start", borderBottom: "1px solid #f1f5f9" }}>
          {SUBTABS.map((t, i) => {
            const [dn, tot] = tabProgress(i);
            const done = tot > 0 && dn === tot;
            const pct = tot > 0 ? Math.round((dn / tot) * 100) : 0;
            const active = sub === i;
            const border = done ? FIELD_FILLED : (active ? "#1976d2" : "#cbd5e1");
            const bg = active ? "#1976d2" : "#ffffff";
            const color = active ? "#ffffff" : (done ? "#15803D" : "#334155");
            return (
              <div key={t} style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "stretch" }}>
                <button onClick={() => setSub(i)}
                  title={done ? "Section complete" : `${dn} of ${tot} completed in this section`}
                  style={{
                    border: `${done ? 2 : 1}px solid ${border}`, background: bg, color,
                    borderRadius: 6, padding: done ? "4px 13px" : "5px 14px",
                    fontSize: 12.5, fontWeight: 700,
                    lineHeight: 1.2, whiteSpace: "nowrap", cursor: "pointer",
                    fontFamily: "inherit", appearance: "none", WebkitAppearance: "none"
                  }}>{t}{done ? " ✓" : ""}</button>
                {/* progress track — full width of the tab above it */}
                <div aria-hidden="true"
                  style={{ height: 5, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                  <div style={{
                    width: `${pct}%`, height: "100%", borderRadius: 999,
                    background: done ? FIELD_FILLED : "#4ADE80",
                    transition: "width 180ms ease"
                  }} />
                </div>
              </div>
            );
          })}
          {locked && <span style={{ marginLeft: "auto", fontSize: 11, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 999, padding: "3px 10px" }}>
            🔒 Completed {new Date(current.completedAt).toLocaleDateString("en-GB")} — record locked</span>}
        </div>

        {err && <div style={{ color: "#dc2626", fontSize: 13, padding: "6px 16px" }}>Error: {err}</div>}

        {/* Body — same frame for every tab */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 18px" }}>

          {/* ------- 0. Narrative & evidence (AI analysis) ------- */}
          {sub === 0 && (<>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <div style={lab}>Review type</div>
                <select style={{ ...inp(sections.reviewType || "annual", locked), width: 200 }} disabled={locked} value={sections.reviewType || "annual"} onChange={e => set("reviewType", e.target.value)}>
                  <option value="annual">Annual review</option>
                  <option value="probation">End of probation</option>
                </select>
              </div>
              <div>
                <div style={lab}>Review period from</div>
                <input type="date" style={{ ...inp(sections.reviewPeriodFrom, locked), width: 160 }} disabled={locked} value={sections.reviewPeriodFrom || ""} onChange={e => set("reviewPeriodFrom", e.target.value)} />
              </div>
              <div>
                <div style={lab}>to</div>
                <input type="date" style={{ ...inp(sections.reviewPeriodTo, locked), width: 160 }} disabled={locked} value={sections.reviewPeriodTo || ""} onChange={e => set("reviewPeriodTo", e.target.value)} />
              </div>
              <div>
                <div style={lab}>Meeting date</div>
                <input type="date" style={{ ...inp(meetingDate, locked), width: 160 }} disabled={locked} value={meetingDate} onChange={e => setMeetingDate(e.target.value)} />
              </div>
            </div>
            <div style={lab}>Automated 12-month evidence pack {locked ? "(snapshot taken at completion)" : "(live — will be snapshotted on completion)"}</div>
            {packLoading ? (
              <div style={{ fontSize: 13, color: "#6b7280", padding: "12px 0" }}>
                Computing the 12-month analysis for {yearTab}… (rebuilding the branch dataset)
              </div>
            ) : pack ? (
              <div style={{ background: TINT.autoPanel, border: `1px solid ${TINT.autoBorder}`, borderRadius: 6, padding: "4px 14px 10px", maxHeight: "42vh", overflowY: "auto" }}>
                <div style={capStyle("auto")}>Auto-generated from the year’s data (read-only)</div>
                <ReviewPackView review={pack} staffName={staffName} masked={masked} />
              </div>
            ) : <div style={{ fontSize: 13, color: "#6b7280" }}>No pack could be computed for {yearTab}.</div>}
            <PreAnswer pre={pre} ids={["agenda"]} label="They asked for this on the meeting agenda (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Appraiser commentary on the evidence" path="narrativeComment"
               ph="Suggested prompts: Does the data match your day-to-day impression of them? Is there context the numbers can't see — covering for colleagues, staffing gaps, unusually busy periods, till vs dispensary time, system changes? Anything in the near-miss or shift data worth exploring in the meeting?" />
          </>)}

          {/* ------- 1. Pre-appraisal (read-only) ------- */}
          {sub === 1 && (<>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ ...lab, margin: 0 }}>Pre-appraisal response from interviewee (read-only)</div>
              <button style={btn("#4338ca")} disabled={!!busy} onClick={sendPre}
                title="Emails the staff member the pre-appraisal questionnaire link. Their answers save automatically as they type and appear here.">
                {busy === "presend" ? "Sending…" : pre ? "✉ Re-send pre-appraisal" : "✉ Send pre-appraisal"}
              </button>
              <button style={btn("#6b7280")} onClick={loadPre}>↻ Refresh</button>
            </div>
            {preLoading ? (
              <div style={{ fontSize: 13, color: "#6b7280", padding: "12px 0" }}>Loading pre-appraisal…</div>
            ) : !pre ? (
              <div style={{ fontSize: 13, color: "#6b7280", padding: "12px 0" }}>
                No pre-appraisal has been sent for {yearTab} yet. Use "Send pre-appraisal" (here, or on the staff grid)
                to email {displayName(staffName, masked)} the questionnaire — it explains the purpose and structure of the
                appraisal, asks a set of community-pharmacy reflection questions, and has them rate themselves 1–5 on
                benchmarks like punctuality, confidentiality, accuracy and SOP knowledge. Their answers save into the
                system as they type and appear on this tab.
              </div>
            ) : (<div style={{ background: TINT.staffPanel, border: `1px solid ${TINT.staffBorder}`, borderRadius: 8, padding: "4px 12px 10px" }}>
              <div style={capStyle("staff")}>Staff member’s own words — from their pre-appraisal form (read-only)</div>
              <div style={{ fontSize: 12.5, margin: "8px 0", color: "#374151" }}>
                Sent to <b>{pre.sentTo}</b> on {pre.sentAt ? new Date(pre.sentAt).toLocaleDateString("en-GB") : "—"}
                {" · "}
                {pre.status === "responded"
                  ? <span style={{ color: "#166534", fontWeight: 700 }}>✓ Submitted {pre.respondedAt ? new Date(pre.respondedAt).toLocaleString("en-GB") : ""}</span>
                  : <span style={{ color: "#92400e", fontWeight: 700 }}>Awaiting submission{pre.lastAnsweredAt ? ` — last saved answer ${new Date(pre.lastAnsweredAt).toLocaleString("en-GB")}` : ""}</span>}
              </div>
              <div style={lab}>Self-ratings (1 = area to develop · 5 = a real strength)</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 8 }}>
                <tbody>
                  {(pre.ratingItems || []).map(r => {
                    const v = pre.ratings?.[r.id];
                    return (
                      <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "5px 6px" }}>{r.label}</td>
                        <td style={{ padding: "5px 6px", width: 130, whiteSpace: "nowrap" }}>
                          {v ? <>
                            <span style={{ letterSpacing: 2 }}>{"●".repeat(v)}{"○".repeat(5 - v)}</span>
                            <b style={{ marginLeft: 6 }}>{v}/5</b>
                          </> : <span style={{ color: "#9ca3af" }}>not answered</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={lab}>Their answers</div>
              {(pre.questions || []).map(q => (
                <div key={q.id} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>{q.q}</div>
                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap", background: TINT.staffBg, border: `1px solid ${TINT.staffBorder}`, borderRadius: 6, padding: "6px 10px", marginTop: 3, fontStyle: "italic", color: pre.answers?.[q.id] ? "#111" : "#9ca3af" }}>
                    {pre.answers?.[q.id] || "(not answered)"}
                  </div>
                </div>
              ))}
            </div>)}
          </>)}

          {/* ------- 2. Performance ------- */}
          {sub === 2 && (<>
            <div style={lab}>Overall assessment of performance (agree a rating for this review period)</div>
            {/* v7: MULTI-BOX SELECTOR — white throughout. While NOTHING is
                selected every option carries the wide black attention
                border, so the unanswered group reads as a single
                outstanding item. Once a rating is picked, that option
                takes the wide green border (and a ✓) and the rest drop back
                to a quiet grey so the choice is unambiguous. */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
              {RATING_SCALE.map(([v, name]) => {
                const isSel = sections.overallRating === v;
                // filledVal treats 0 ("Not applicable") as a real answer.
                const anySel = filledVal(sections.overallRating);
                const bd = locked ? (isSel ? FIELD_FILLED : FIELD_LOCKED)
                         : isSel   ? FIELD_FILLED
                         : anySel  ? FIELD_MUTED
                         :           FIELD_EMPTY;
                return (
                  <button key={v} disabled={locked}
                    onClick={() => set("overallRating", v)}
                    style={{
                      border: `2px solid ${bd}`, background: "#ffffff",
                      color: isSel ? "#15803D" : anySel ? "#64748B" : "#111827",
                      fontWeight: isSel ? 800 : 600,
                      borderRadius: 4, padding: "4px 9px", fontSize: 12,
                      fontFamily: "inherit", cursor: locked ? "default" : "pointer",
                      appearance: "none", WebkitAppearance: "none"
                    }}>{isSel ? "✓ " : ""}{v} — {name}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, minHeight: 30 }}>
              {sections.overallRating !== null && sections.overallRating !== undefined
                ? RATING_SCALE.find(r => r[0] === sections.overallRating)?.[2]
                : "Select a rating — the definition will appear here. 5 Outstanding · 4 Excellent · 3 Fully effective · 2 Approaching expectations · 1 Under-performing · 0 Not applicable."}
            </div>
            <Field sections={sections} set={set} locked={locked} label="Summary of overall performance" path="ratingSummary"
               ph="Suggested prompts: Summarise performance over the period, relating strengths and weaknesses to what matters in THIS role — accuracy at the bench, patient manner at the counter, reliability of date checking/fridge logs, contribution to services (Pharmacy First, NMS, flu), how they respond under pressure…" />
            <PreRatings pre={pre} ids={["patientCare", "communication", "teamwork"]} label="Their self-ratings — patient care & team (pre-appraisal)" />
            <PreAnswer pre={pre} ids={["wentWell"]} label="Their answer — what went well (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="What went well this year" path="performance.wentWell"
               ph="Suggested prompts: Which parts of the role have they clearly mastered? Positive patient feedback or compliments? Services they've driven? Times they went beyond the job — covering shifts, training others, handling difficult situations well?" />
            <PreAnswer pre={pre} ids={["proudOf"]} label="Their answer — achievements they're proud of (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Achievements" path="performance.achievements"
               ph="Suggested prompts: Qualifications gained or in progress (NVQ, ACPT, dispensing course)? Audits completed? Near-miss reductions? New services launched or targets hit? Mentoring of new starters?" />
            <PreRatings pre={pre} ids={["accuracy", "sops", "medicines", "pressure"]} label="Their self-ratings — accuracy, SOPs & pressure (pre-appraisal)" />
            <PreAnswer pre={pre} ids={["challenges"]} label="Their answer — what they find challenging (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Areas to develop" path="performance.develop"
               ph="Suggested prompts: Where do errors or near misses cluster? Any confidence gaps — CDs, fridge lines, owings, EPS problem-solving? SOP knowledge needing a refresh? Communication, phone manner or till work to build? Speed vs accuracy balance?" />
          </>)}

          {/* ------- 3. Training & development ------- */}
          {sub === 3 && (<>
            <Field sections={sections} set={set} locked={locked} label="CPD / training completed this year" path="training.cpdDone"
               ph="Suggested prompts: Which courses, e-learning or in-house training were completed? GPhC revalidation entries up to date (registered professionals)? Anything started but not finished — why?" />
            <PreRatings pre={pre} ids={["learning", "safeguarding", "confidentiality"]} label="Their self-ratings — learning, safeguarding & confidentiality (pre-appraisal)" />
            <PreAnswer pre={pre} ids={["training"]} label="Their answer — training they'd like (pre-appraisal)" />
            <div style={lab}>Training / development plan (need → aim → method → support → cost → timescale)</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={smallTh}>Development need</th><th style={smallTh}>Aim</th>
                <th style={smallTh}>Method</th><th style={smallTh}>Support required</th>
                <th style={{ ...smallTh, width: 80 }}>Est. cost</th><th style={{ ...smallTh, width: 110 }}>Timescale</th>
                {!locked && <th style={{ width: 30 }} />}
              </tr></thead>
              <tbody>
                {(sections.trainingPlan || []).map((t, i) => (
                  <tr key={i}>
                    <td><input style={inp(t.need, locked)} disabled={locked} placeholder="e.g. Consultation confidence" value={t.need || ""} onChange={e => set(`trainingPlan.${i}.need`, e.target.value)} /></td>
                    <td><input style={inp(t.aim, locked)} disabled={locked} placeholder="e.g. Deliver Pharmacy First alone" value={t.aim || ""} onChange={e => set(`trainingPlan.${i}.aim`, e.target.value)} /></td>
                    <td><input style={inp(t.method, locked)} disabled={locked} placeholder="e.g. CPPE module + shadowing" value={t.method || ""} onChange={e => set(`trainingPlan.${i}.method`, e.target.value)} /></td>
                    <td><input style={inp(t.support, locked)} disabled={locked} placeholder="e.g. Protected time; mgr feedback" value={t.support || ""} onChange={e => set(`trainingPlan.${i}.support`, e.target.value)} /></td>
                    <td><input style={inp(t.cost, locked)} disabled={locked} placeholder="£" value={t.cost || ""} onChange={e => set(`trainingPlan.${i}.cost`, e.target.value)} /></td>
                    <td><input style={inp(t.timescale, locked)} disabled={locked} placeholder="e.g. by Q3" value={t.timescale || ""} onChange={e => set(`trainingPlan.${i}.timescale`, e.target.value)} /></td>
                    {!locked && <td><button style={btn("#dc2626")} onClick={() => set("trainingPlan", sections.trainingPlan.filter((_, j) => j !== i))}>✕</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {!locked && <button style={{ ...btn("#1976d2"), marginTop: 6 }} onClick={() => set("trainingPlan", [...(sections.trainingPlan || []), {}])}>+ Add development need</button>}
            <Field sections={sections} set={set} locked={locked} label="Training needs — free commentary (link to training register items where possible)" path="training.needsSummary"
               ph="Suggested prompts: Safeguarding level due for refresh? ACDA / accuracy checking ambitions? Emergency-supply, CD or fridge-failure procedures confident? First aid / fire marshal cover for the branch?" />
            <Field sections={sections} set={set} locked={locked} label="Revalidation support (registered professionals)" path="training.revalidationNote"
               ph="Suggested prompts: Entries on track for renewal? Peer discussion arranged? Reflective accounts mapped to the standards?" />
          </>)}

          {/* ------- 4. Objectives ------- */}
          {sub === 4 && (<>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ ...lab, margin: 0 }}>Objectives from the last review — results achieved</div>
              {!locked && <button style={btn("#4338ca")} onClick={seedLastObjectives}
                title={`Copies the objectives recorded on the ${yearTab - 1} appraisal into this table so results can be recorded against them.`}>
                ⤓ Copy from {yearTab - 1}</button>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
              <thead><tr>
                <th style={smallTh}>Objective</th><th style={smallTh}>How measured</th>
                <th style={smallTh}>Results achieved</th><th style={smallTh}>Comments — differences, strengths, areas to improve</th>
                {!locked && <th style={{ width: 30 }} />}
              </tr></thead>
              <tbody>
                {(sections.lastObjectives || []).map((o, i) => (
                  <tr key={i}>
                    <td><input style={inp(o.title, locked)} disabled={locked} placeholder="Objective set last review" value={o.title || ""} onChange={e => set(`lastObjectives.${i}.title`, e.target.value)} /></td>
                    <td><input style={inp(o.measure, locked)} disabled={locked} placeholder="Measurement agreed" value={o.measure || ""} onChange={e => set(`lastObjectives.${i}.measure`, e.target.value)} /></td>
                    <td><input style={inp(o.result, locked)} disabled={locked} placeholder="What was actually achieved" value={o.result || ""} onChange={e => set(`lastObjectives.${i}.result`, e.target.value)} /></td>
                    <td><input style={inp(o.comments, locked)} disabled={locked} placeholder="Why the difference? What does it show?" value={o.comments || ""} onChange={e => set(`lastObjectives.${i}.comments`, e.target.value)} /></td>
                    {!locked && <td><button style={btn("#dc2626")} onClick={() => set("lastObjectives", sections.lastObjectives.filter((_, j) => j !== i))}>✕</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {!locked && <button style={{ ...btn("#1976d2"), marginTop: 6 }} onClick={() => set("lastObjectives", [...(sections.lastObjectives || []), {}])}>+ Add previous objective</button>}

            <div style={lab}>SMART objectives for the coming year (Specific · Measurable · Achievable · Realistic · Timebound)</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={smallTh}>Objective</th><th style={smallTh}>How measured</th>
                <th style={smallTh}>Support required</th><th style={{ ...smallTh, width: 130 }}>Deadline</th>
                <th style={smallTh}>Review notes & date</th>
                {!locked && <th style={{ width: 30 }} />}
              </tr></thead>
              <tbody>
                {(sections.objectives || []).map((o, i) => (
                  <tr key={i}>
                    <td><input style={inp(o.title, locked)} disabled={locked} placeholder="e.g. Reduce dispensing near misses" value={o.title || ""} onChange={e => set(`objectives.${i}.title`, e.target.value)} /></td>
                    <td><input style={inp(o.measure, locked)} disabled={locked} placeholder="e.g. From 6/mo to 3/mo on the register" value={o.measure || ""} onChange={e => set(`objectives.${i}.measure`, e.target.value)} /></td>
                    <td><input style={inp(o.support, locked)} disabled={locked} placeholder="e.g. Bench re-layout; mgr check-ins" value={o.support || ""} onChange={e => set(`objectives.${i}.support`, e.target.value)} /></td>
                    <td><input type="date" style={inp(o.targetDate, locked)} disabled={locked} value={o.targetDate || ""} onChange={e => set(`objectives.${i}.targetDate`, e.target.value)} /></td>
                    <td><input style={inp(o.reviewNotes, locked)} disabled={locked} placeholder="Progress noted at mid-year check-in…" value={o.reviewNotes || ""} onChange={e => set(`objectives.${i}.reviewNotes`, e.target.value)} /></td>
                    {!locked && <td><button style={btn("#dc2626")} onClick={() => set("objectives", sections.objectives.filter((_, j) => j !== i))}>✕</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {!locked && <button style={{ ...btn("#1976d2"), marginTop: 6 }} onClick={() => set("objectives", [...(sections.objectives || []), {}])}>+ Add objective</button>}
          </>)}

          {/* ------- 5. Wellbeing & general ------- */}
          {sub === 5 && (<>
            <PreAnswer pre={pre} ids={["workload", "wellbeing"]} label="Their answers — workload & wellbeing (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Workload & wellbeing discussion" path="wellbeing.workload"
               ph="Suggested prompts: How are they finding workload and staffing day to day? Breaks being taken? Pressure points in the week (rhythm of scripts, deliveries, MDS)? Anything outside work they wish to share that affects them at work?" />
            <PreAnswer pre={pre} ids={["career"]} label="Their answer — career aspirations (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Career aspirations" path="wellbeing.aspirations"
               ph="Suggested prompts: Where do they see themselves in 1–3 years — accuracy checking, NVQ2/3, services lead, management, prescribing support? What would make them stay and grow here?" />
            <Field sections={sections} set={set} locked={locked} label="Potential — appraiser's comment" path="wellbeing.potential"
               ph="Suggested prompts: Comment on their potential against those aspirations. What training and development would be necessary to achieve them? Realistic timescale?" />
            <Field sections={sections} set={set} locked={locked} label="Support agreed" path="wellbeing.supportAgreed"
               ph="e.g. Protected training time Thursday afternoons; monthly 1:1s; adjust rota around college days…" />
            <PreRatings pre={pre} ids={["punctuality", "professionalism"]} label="Their self-ratings — punctuality & professionalism (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Areas of concern (action to be taken and by whom)" path="general.concerns"
               ph="Suggested prompts: Attendance or punctuality patterns? Conduct or professionalism issues? Competence concerns and the support plan? Record the agreed action AND the owner." />
            <PreAnswer pre={pre} ids={["ideas", "safety"]} label="Their answers — improvement ideas & patient-safety points (pre-appraisal)" />
            <Field sections={sections} set={set} locked={locked} label="Suggestions for improvement (business/service ideas from the staff member)" path="general.suggestions"
               ph="Suggested prompts: Workflow or bench layout ideas? Opening-hours or service suggestions? Anything patients keep asking for that we don't do? Stock or ordering frustrations worth fixing?" />
            <Field sections={sections} set={set} locked={locked} label="Any further comments / issues" path="general.further"
               ph="Anything discussed that doesn't fit the sections above." />
          </>)}

          {/* ------- 6. Sign-off ------- */}
          {sub === 6 && (<>
            <Field sections={sections} set={set} locked={locked} label="Staff member's own comments" path="staffComments"
               ph="Their words, recorded verbatim where possible — do they agree with the rating and summary? Anything they want on the record?" />
            {!locked ? (<>
              <div style={lab}>Appraiser sign-off (type full name) — Manager / Group Manager only</div>
              <input style={{ ...inp(signAppraiser, false), width: 280 }} value={signAppraiser} onChange={e => setSignAppraiser(e.target.value)} />
              <div style={lab}>Staff member sign-off (optional, type full name)</div>
              <input style={{ ...inpOpt(signStaff), width: 280 }} value={signStaff} onChange={e => setSignStaff(e.target.value)} />
              <div style={{ marginTop: 14 }}>
                <button style={{ ...btn("#fff"), background: "#1976d2", color: "#fff", border: "none", padding: "10px 20px" }}
                  disabled={!!busy} onClick={complete}>
                  {busy === "complete" ? "Completing…" : "Complete appraisal (locks the record & snapshots the evidence pack)"}
                </button>
              </div>
            </>) : (
              <div style={{ fontSize: 13, color: "#374151", marginTop: 8 }}>
                Signed: {current.signatures?.appraiser?.name} (appraiser){current.signatures?.staff?.name ? `; ${current.signatures.staff.name} (staff)` : ""} — {new Date(current.completedAt).toLocaleString("en-GB")}
                <div style={{ marginTop: 12 }}>
                  <button style={btn("#4338ca")} disabled={!!busy} onClick={openSummaryPreview}
                    title="Shows you exactly what will be emailed to the staff member and lets you edit it before sending. The sent text is stored on the Summary sent tab.">
                    {busy === "send" ? "…" : current?.sentToStaffAt ? "✉ Preview & re-send summary" : "✉ Preview & send summary to staff"}
                  </button>
                </div>
              </div>)}
          </>)}

          {/* ------- 7. Follow-ups ------- */}
          {sub === 7 && (<>
            <div style={lab}>Agreed follow-up actions (tickable after completion; overdue items surface on the head-office panel)</div>
            {(sections.followUps || []).map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <input type="checkbox" checked={!!f.done} onChange={async e => {
                  set(`followUps.${i}.done`, e.target.checked);
                  if (locked) { try { await post("/api/appraisals/followup", { pharmacyName, staffId, appraisalYear: yearTab, index: i, done: e.target.checked }); } catch (_) {} }
                }} />
                <input style={{ ...inp(f.action, locked), flex: 3 }} disabled={locked} placeholder="Action — e.g. Book safeguarding refresher; review near-miss trend at 3 months" value={f.action || ""} onChange={e => set(`followUps.${i}.action`, e.target.value)} />
                <input style={{ ...inp(f.owner, locked), flex: 1 }} disabled={locked} placeholder="Owner" value={f.owner || ""} onChange={e => set(`followUps.${i}.owner`, e.target.value)} />
                <input type="date" style={{ ...inp(f.dueDate, locked), width: 150 }} disabled={locked} value={f.dueDate || ""} onChange={e => set(`followUps.${i}.dueDate`, e.target.value)} />
                {!locked && <button style={btn("#dc2626")} onClick={() => set("followUps", sections.followUps.filter((_, j) => j !== i))}>✕</button>}
              </div>))}
            {!locked && (<>
              <button style={btn("#1976d2")} onClick={() => set("followUps", [...(sections.followUps || []), {}])}>+ Add action</button>
              <button style={{ ...btn("#4338ca"), marginLeft: 6 }} onClick={() => set("followUps", [...(sections.followUps || []), { action: "Mid-year check-in", owner: "Manager" }])}>+ Mid-year check-in</button>
            </>)}
          </>)}

          {/* ------- 8. Emails & summary (final page) ------- */}
          {sub === 8 && (<>
            <div style={lab}>Emails sent regarding this appraisal</div>
            {(current?.emailsLog || []).length ? (
              (current.emailsLog || []).map((m, i) => (
                <details key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", marginBottom: 6, fontSize: 13, background: "#fff" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    <span style={{ display: "inline-block", minWidth: 130, color: m.type === "pre_appraisal" ? "#4338ca" : "#1976d2" }}>
                      {m.type === "pre_appraisal" ? "Pre-appraisal form" : m.type === "appraisal_invite" ? "Meeting invitation" : m.type === "appraiser_form" ? "Appraiser form link" : m.type}
                    </span>
                    to {m.to} — {m.sentAt ? new Date(m.sentAt).toLocaleString("en-GB") : "—"}
                  </summary>
                  <div style={{ fontSize: 12, color: "#374151", margin: "8px 0 4px" }}><b>Subject:</b> {m.subject}</div>
                  <div style={{ whiteSpace: "pre-wrap", fontFamily: "Consolas, Menlo, monospace", fontSize: 12, background: "#f8fafc", border: "1px solid #eef0f3", borderRadius: 6, padding: "8px 10px" }}>{m.body}</div>
                </details>
              ))
            ) : (
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                No emails logged yet. Booking the appraisal from the staff grid logs the meeting invitation and
                pre-appraisal questionnaire here verbatim.
              </div>
            )}

            <div style={lab}>Summary sent to the staff member — exact text snapshot</div>
            {current?.sentSummary ? (<>
              <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 8 }}>
                Sent to <b>{current.sentSummary.to}</b> on <b>{new Date(current.sentSummary.sentAt).toLocaleString("en-GB")}</b>.
                This is a verbatim record of the email — part of the appraisal document.
              </div>
              <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 14px", fontSize: 13, whiteSpace: "pre-wrap", fontFamily: "Consolas, Menlo, monospace" }}>
                {current.sentSummary.text}
              </div>
              {locked && (
                <div style={{ marginTop: 10 }}>
                  <button style={btn("#4338ca")} disabled={!!busy} onClick={openSummaryPreview}
                    title="Re-opens the editor seeded with the text sent last time. Sending again replaces this snapshot.">
                    {busy === "send" ? "…" : "✉ Preview & re-send"}
                  </button>
                </div>
              )}
            </>) : current?.sentToStaffAt ? (
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                A summary was sent on {new Date(current.sentToStaffAt).toLocaleDateString("en-GB")} to {current.emailTo || "the staff member"},
                before text snapshots were stored. Re-sending from the Sign-off tab will record the exact text here.
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                No summary has been sent for {yearTab} yet. Complete the appraisal on the Sign-off tab, then use
                "Preview &amp; send summary" — you'll see exactly what will be emailed, can edit it, and the sent
                version is stored here permanently as the final page of the appraisal document.
              </div>
            )}
          </>)}
        </div>

        </>)}

        {/* v3.3: summary preview / edit modal */}
        {summaryModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300 }}>
            <div style={{ background: "#fff", borderRadius: 8, width: "min(760px, 92vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <b style={{ fontSize: 15 }}>Summary email — review &amp; edit before sending</b>
                <button style={btn("#6b7280")} onClick={() => setSummaryModal(null)}>✕ Cancel</button>
              </div>
              <div style={{ padding: "10px 16px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f1f5f9" }}>
                To: <b>{summaryModal.to}</b> &nbsp;·&nbsp; Subject: <b>{summaryModal.subject}</b>
                {summaryModal.previouslySentAt && <span style={{ color: "#92400e" }}> &nbsp;·&nbsp; previously sent {new Date(summaryModal.previouslySentAt).toLocaleString("en-GB")} — sending again replaces the stored snapshot</span>}
              </div>
              <textarea
                style={{ flex: 1, minHeight: 340, margin: 16, marginBottom: 8, padding: "10px 12px", border: "1px solid #9ca3af", borderRadius: 6, fontSize: 13, fontFamily: "Consolas, Menlo, monospace", lineHeight: 1.45, resize: "vertical" }}
                value={summaryModal.text}
                onChange={e => setSummaryModal(m => ({ ...m, text: e.target.value }))} />
              <div style={{ padding: "0 16px 14px", display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#6b7280", marginRight: "auto" }}>Edit freely — exactly what you see is what is sent and stored on the record.</span>
                <button style={{ ...btn("#fff"), background: "#4338ca", color: "#fff", border: "none", padding: "9px 18px" }}
                  disabled={!!busy} onClick={sendSummary}>
                  {busy === "send" ? "Sending…" : "Send summary"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}